// Console-owned `PlatformDataAdapter` implementation (fn-47.5).
//
// `@sb-ai-platform/ui-react` is headless by design: components consume
// data exclusively through the `PlatformDataAdapter` seam supplied via
// `<PlatformProvider>`. This module is the console's implementation —
// the ONLY place where the package's view-model contract meets the
// console's transports:
//
//   - Queries + mutations ride the existing tRPC vanilla client
//     (`createVanillaTrpcClient` — httpLink + cookies + CSRF header +
//     refresh-retry, all inherited from `buildTrpcLinks`).
//   - The live event stream rides the existing `EventSource` wrapper
//     (`streams/session-stream.ts`) with the URL built from
//     `bffEndpoints().sseBaseUrl` — split-origin rule: every browser→BFF
//     URL goes through that helper, NEVER hand-built (memory:
//     split-origin-migration-must-lift-all-2026-06-02).
//
// **Mapping discipline:** the BFF returns the CP's snake_case wire
// shapes; the package's view-model types deliberately mirror them
// (`InboxSession.user_id`, `next_cursor` → `nextCursor`), so mapping is
// a thin, pure pass-through — kept in standalone functions and
// unit-tested with a fake tRPC client (`platform-adapter.test.ts`).

import type {
  AgentSummary,
  ApprovalItem,
  DecisionResult,
  InboxSession,
  Page,
  PlatformDataAdapter,
  SessionEventEnvelope,
  SessionStatus,
} from '@sb-ai-platform/ui-react';
import { DecisionError } from '@sb-ai-platform/ui-react';

import { type BffEndpoints, bffEndpoints } from './auth/bff-origin.js';
import {
  buildSessionStreamUrl,
  connectSessionStream as defaultConnectSessionStream,
} from './streams/session-stream.js';
import { createVanillaTrpcClient } from './trpc-client.js';

// ── Wire shapes (structural mirrors of the BFF router outputs) ──────
//
// We re-declare the handful of fields the adapter reads rather than
// deep-importing the BFF router modules: the console only depends on
// `@sb-ai-platform/bff`'s exported `BffRouter` type, and the real
// vanilla client is structurally assignable to `InboxTrpcClient`
// below — `createConsolePlatformAdapter` pins that at the call site.

interface WireRunRow {
  readonly id: string;
  readonly user_id: string;
  readonly agent_id: string;
  readonly matter_id: string | null;
  readonly status: string;
  readonly created_at: string;
  readonly last_updated_at: string;
  readonly terminal_at: string | null;
  readonly metadata: Record<string, unknown> | null;
}

interface WireRunEventEnvelope {
  readonly id: string;
  readonly type: string;
  readonly data: Record<string, unknown>;
  readonly smokeball?: Record<string, unknown>;
}

interface WireApprovalRow {
  readonly id: string;
  readonly session_id: string;
  readonly ack_kind:
    | 'tool_confirmation'
    | 'custom_tool_result_immediate'
    | 'custom_tool_result_deferred';
  readonly tool_kind: 'builtin' | 'mcp' | 'custom';
  readonly tool_name: string;
  readonly tool_args: Record<string, unknown>;
  readonly created_at: string;
}

/**
 * The slice of the BFF `AgentRow` the adapter reads. The full wire
 * row also carries `prompt` (the system prompt) — deliberately NOT
 * declared here: system prompts are not end-user copy and must never
 * be mapped into `AgentSummary.description` (fn-48 R1).
 */
interface WireAgentRow {
  readonly id: string;
  readonly name: string;
  /**
   * fn-75 / ADR 0035 — derived soft-archive flag from the BFF `AgentRow`
   * (`true` iff the CP row's `status='archived'`). Mapped verbatim onto
   * `AgentSummary.archived`.
   */
  readonly archived: boolean;
  /** ADR 0043 — the agent's memory attachment map (informational, read-only). */
  readonly memory?: {
    readonly user?: 'read_only' | 'read_write';
    readonly firm?: 'read_only';
    readonly matter?: 'read_only' | 'read_write';
  };
}

interface WirePage<T> {
  readonly data: readonly T[];
  readonly next_cursor?: string | null;
}

/**
 * The slice of the tRPC vanilla client the adapter consumes. The real
 * `CreateTRPCClient<BffRouter>` is structurally assignable to this;
 * tests inject a fake with the same shape.
 */
export interface InboxTrpcClient {
  readonly runs: {
    readonly list: {
      query(input: {
        status?: string;
        cursor?: string;
        limit?: number;
        includeArchived?: boolean;
      }): Promise<WirePage<WireRunRow>>;
    };
    readonly byId: {
      query(input: { sessionId: string }): Promise<WireRunRow>;
    };
    readonly events: {
      query(input: {
        sessionId: string;
        afterId?: string;
        limit?: number;
        order?: 'asc' | 'desc';
      }): Promise<WirePage<WireRunEventEnvelope>>;
    };
    readonly sendMessage: {
      mutate(input: { sessionId: string; content: string }): Promise<unknown>;
    };
    readonly start: {
      mutate(input: {
        agentId: string;
        matterId?: string;
        metadata?: Record<string, unknown>;
      }): Promise<{ id: string; status: string }>;
    };
  };
  readonly agents: {
    readonly list: {
      query(input: {
        cursor?: string;
        limit?: number;
        includeArchived?: boolean;
      }): Promise<WirePage<WireAgentRow>>;
    };
  };
  readonly approvals: {
    readonly list: {
      query(input: { cursor?: string; limit?: number }): Promise<WirePage<WireApprovalRow>>;
    };
    readonly pendingCount: {
      query(): Promise<{ count: number; truncated: boolean }>;
    };
    readonly decide: {
      mutate(input: {
        sessionId: string;
        approvalId: string;
        decision: 'approved' | 'rejected';
        denyMessage?: string;
        formData?: Record<string, unknown>;
        notes?: string;
      }): Promise<{
        approval_id: string;
        decision: 'approved' | 'rejected';
        ack_dispatched: boolean;
        ack_kind:
          | 'tool_confirmation'
          | 'custom_tool_result_immediate'
          | 'custom_tool_result_deferred';
        redrive?: boolean;
      }>;
    };
  };
}

// ── Pure mapping helpers (exported for unit tests) ──────────────────

/**
 * Map one BFF run row to the package's `InboxSession` view model. The
 * fields are snake_case pass-throughs by design (the package mirrors
 * the CP wire shape so adapters don't rename).
 *
 * `status` is a plain `string` on the BFF wire; the CP only ever emits
 * the 8 `SessionStatus` values, and the package's `StatusPill`
 * tolerates a genuinely-unrecognized value by falling through to its
 * `unknown` presentation — so we pass the value through rather than
 * dropping rows on a vocabulary mismatch.
 */
export function toInboxSession(row: WireRunRow): InboxSession {
  return {
    id: row.id,
    status: row.status as SessionStatus,
    agent_id: row.agent_id,
    user_id: row.user_id,
    matter_id: row.matter_id,
    created_at: row.created_at,
    last_updated_at: row.last_updated_at,
    terminal_at: row.terminal_at,
    metadata: row.metadata,
  };
}

/**
 * Map one nested `/events` envelope to the package's
 * `SessionEventEnvelope`. The package's stream layer normalizes BOTH
 * wire shapes (flat SSE vs nested `{id, type, data, smokeball?}`) —
 * the seed path hands over the nested object verbatim under `data`
 * and lets `normalizeWireEvent` do its job.
 */
export function toSessionEventEnvelope(event: WireRunEventEnvelope): SessionEventEnvelope {
  return { id: event.id, data: event };
}

/**
 * Map one BFF agent row to the package's `AgentSummary`. `name` + the
 * derived `archived` flag (fn-75) — `AgentRow` has no safe display
 * description, and the agent `prompt` must NEVER be mapped to
 * `description` (system prompts are not end-user copy). `description`
 * stays absent from this adapter until a display description lands on
 * the agent contract.
 */
export function toAgentSummary(row: WireAgentRow): AgentSummary {
  return {
    id: row.id,
    name: row.name,
    archived: row.archived,
    // ADR 0043 — surface the agent's memory attachment map so the start-chat
    // picker can show where memory is enabled and at what access mode.
    ...(row.memory !== undefined ? { memory: row.memory } : {}),
  };
}

/**
 * Title-precedence merge for `startSession` (fn-48 R1): a dedicated
 * `title` arg that trims non-empty WINS over any `metadata.title` the
 * caller also supplied; when there is no usable title, `metadata`
 * passes through UNTOUCHED (never write `title: undefined` — the CP
 * stores metadata verbatim, and a literal undefined/null key would
 * pollute the jsonb).
 */
export function withTitleMetadata(
  metadata: Record<string, unknown> | undefined,
  title: string | undefined,
): Record<string, unknown> | undefined {
  const trimmed = title?.trim();
  if (trimmed !== undefined && trimmed.length > 0) {
    return { ...(metadata ?? {}), title: trimmed };
  }
  return metadata;
}

function toPage<W, V>(page: WirePage<W>, map: (row: W) => V): Page<V> {
  return {
    data: page.data.map(map),
    nextCursor: page.next_cursor ?? null,
  };
}

/**
 * Extract the tRPC error code from an unknown rejection, defensively
 * (`TRPCClientError.data.code`). Shape-based rather than `instanceof`
 * so the mapping stays pure + fake-friendly in tests.
 */
function trpcErrorCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return undefined;
  const code = (data as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function hasErrorData(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const data = (err as { data?: unknown }).data;
  return typeof data === 'object' && data !== null;
}

function rejectionMessage(err: unknown): string {
  if (err instanceof Error && err.message.length > 0) return err.message;
  return String(err);
}

/**
 * Map a tRPC rejection from `approvals.decide` to the package's typed
 * `DecisionError` (fn-47 adapter contract — components only ever
 * branch on `kind`):
 *
 *   - `CONFLICT`                 → `'conflict'`  (decided elsewhere; CP 409)
 *   - `FORBIDDEN`/`UNAUTHORIZED` → `'forbidden'` (caller may not decide)
 *   - no server error envelope   → `'network'`   (fetch rejection / timeout
 *     — a `TRPCClientError` from a transport failure carries no `data`)
 *   - anything else              → `'other'`
 */
export function toDecisionError(err: unknown): DecisionError {
  if (err instanceof DecisionError) return err;
  const code = trpcErrorCode(err);
  const message = rejectionMessage(err);
  const options = err instanceof Error ? { cause: err } : {};
  if (code === 'CONFLICT') return new DecisionError('conflict', message, options);
  if (code === 'FORBIDDEN' || code === 'UNAUTHORIZED') {
    return new DecisionError('forbidden', message, options);
  }
  if (!hasErrorData(err)) return new DecisionError('network', message, options);
  return new DecisionError('other', message, options);
}

// ── Adapter factory ──────────────────────────────────────────────────

export interface CreateConsolePlatformAdapterOptions {
  /** tRPC client override for tests. Defaults to the console's vanilla client. */
  readonly client?: InboxTrpcClient;
  /** BFF endpoint set override for tests. Defaults to `bffEndpoints()`. */
  readonly endpoints?: BffEndpoints;
  /** EventSource connect primitive override for tests. */
  readonly connectFn?: typeof defaultConnectSessionStream;
}

/**
 * Build the console's `PlatformDataAdapter`. All three transports are
 * injectable for tests; production callers use the zero-arg form (the
 * tRPC URL AND the SSE base both come from `bffEndpoints()` — the
 * single split-origin source of truth).
 */
export function createConsolePlatformAdapter(
  opts: CreateConsolePlatformAdapterOptions = {},
): PlatformDataAdapter {
  const endpoints = opts.endpoints ?? bffEndpoints();
  // The real vanilla client is structurally assignable to the slice the
  // adapter consumes; pin it here so a BFF router drift fails the
  // console type-check instead of exploding at runtime.
  const client: InboxTrpcClient =
    opts.client ?? createVanillaTrpcClient({ trpcUrl: endpoints.trpcUrl });
  const connectFn = opts.connectFn ?? defaultConnectSessionStream;

  return {
    async listSessions(q) {
      const page = await client.runs.list.query({
        ...(q.status !== undefined ? { status: q.status } : {}),
        ...(q.cursor !== undefined ? { cursor: q.cursor } : {}),
        ...(q.limit !== undefined ? { limit: q.limit } : {}),
        ...(q.includeArchived !== undefined ? { includeArchived: q.includeArchived } : {}),
      });
      return toPage(page, toInboxSession);
    },

    async getSession(id) {
      const row = await client.runs.byId.query({ sessionId: id });
      return toInboxSession(row);
    },

    async getSessionEvents(q) {
      const page = await client.runs.events.query({
        sessionId: q.sessionId,
        ...(q.afterId !== undefined ? { afterId: q.afterId } : {}),
        ...(q.limit !== undefined ? { limit: q.limit } : {}),
        ...(q.order !== undefined ? { order: q.order } : {}),
      });
      return toPage(page, toSessionEventEnvelope);
    },

    async listApprovals(q) {
      const page = await client.approvals.list.query({
        ...(q.cursor !== undefined ? { cursor: q.cursor } : {}),
        ...(q.limit !== undefined ? { limit: q.limit } : {}),
      });
      return toPage(
        page,
        (row): ApprovalItem => ({
          id: row.id,
          session_id: row.session_id,
          ack_kind: row.ack_kind,
          tool_kind: row.tool_kind,
          tool_name: row.tool_name,
          tool_args: row.tool_args,
          created_at: row.created_at,
        }),
      );
    },

    async pendingApprovalCount() {
      return client.approvals.pendingCount.query();
    },

    async decideApproval(input): Promise<DecisionResult> {
      try {
        return await client.approvals.decide.mutate({
          sessionId: input.sessionId,
          approvalId: input.approvalId,
          decision: input.decision,
          ...(input.denyMessage !== undefined ? { denyMessage: input.denyMessage } : {}),
          ...(input.formData !== undefined ? { formData: input.formData } : {}),
          ...(input.notes !== undefined ? { notes: input.notes } : {}),
        });
      } catch (err: unknown) {
        throw toDecisionError(err);
      }
    },

    async sendMessage(input) {
      await client.runs.sendMessage.mutate({
        sessionId: input.sessionId,
        content: input.content,
      });
    },

    async listAgents(q = {}) {
      const page = await client.agents.list.query({
        ...(q.cursor !== undefined ? { cursor: q.cursor } : {}),
        ...(q.limit !== undefined ? { limit: q.limit } : {}),
        ...(q.includeArchived !== undefined ? { includeArchived: q.includeArchived } : {}),
      });
      return toPage(page, toAgentSummary);
    },

    async startSession(input) {
      // Create the session, then deliver the first message via the
      // messages route. `runs.start` DOES accept `initialMessage`, but
      // the CP only parks it on `metadata.initial_message` — nothing
      // flushes it since the worker tier was deleted (ADR 0009), so a
      // start without an explicit send leaves the session sitting in
      // `pending` with the user's message silently undelivered
      // (verified against packages/control-plane/src/routes/sessions.ts
      // — the messages route explicitly accepts `pending` sessions and
      // flips them to `running`). We therefore send for real and skip
      // `initialMessage` entirely (passing both would double-deliver
      // the day the CP grows a flush path).
      //
      // `matterId` is forwarded only when supplied — the BFF mints a
      // synthetic matter UUID otherwise (dev convenience; prod tenancy
      // may 403 — documented constraint, not a capability).
      const metadata = withTitleMetadata(input.metadata, input.title);
      // ADR 0043 — memory attachment is defined by the agent, not the session,
      // so nothing memory-related is forwarded on start.
      const started = await client.runs.start.mutate({
        agentId: input.agentId,
        ...(input.matterId !== undefined ? { matterId: input.matterId } : {}),
        ...(metadata !== undefined ? { metadata } : {}),
      });
      await client.runs.sendMessage.mutate({
        sessionId: started.id,
        content: input.message,
      });
      return { sessionId: started.id };
    },

    connectSessionStream(opts2) {
      // Split-origin rule: the stream URL is built from
      // `bffEndpoints().sseBaseUrl` — NEVER hand-assembled. In deployed
      // mode this is the absolute BFF origin (where the `__Host-`
      // cookies live); in dev it stays relative for the Vite proxy.
      const url = buildSessionStreamUrl(opts2.sessionId, endpoints.sseBaseUrl);
      const handle = connectFn({ url });
      // The package's contract: report drops as `'connecting'` (the
      // browser EventSource auto-reconnects), opens as `'open'`. The
      // handle keeps listeners attached until close().
      handle.onOpen(() => opts2.onConnectionChange('open'));
      handle.onError(() => opts2.onConnectionChange('connecting'));
      handle.onMessage((frame) => {
        opts2.onEvent({
          // Flat SSE frames usually embed their MA event id in the
          // payload (the package's normalizer prefers it); the SSE
          // `id:` line rides along as the out-of-band fallback.
          id: frame.lastEventId,
          data: frame.data,
          raw: frame.raw,
        });
      });
      return { close: () => handle.close() };
    },
  };
}

// ── Module-level singleton for the app routes ────────────────────────
//
// The inbox routes share ONE adapter instance (the underlying vanilla
// tRPC client + endpoint set are stateless, so a singleton is safe and
// avoids re-creating the client on every render). Lazy so importing
// this module in tests doesn't eagerly construct a client.

let singleton: PlatformDataAdapter | null = null;

export function getConsolePlatformAdapter(): PlatformDataAdapter {
  if (singleton === null) singleton = createConsolePlatformAdapter();
  return singleton;
}
